import { useEffect, useState } from 'react';
import { tryParse } from 'utils/dataUtils';
import { FeatureToggle, TestType, featureToggles } from 'featureToggles';
import {
  Experiment,
  getExperiment,
  getQueryParam,
  getExperimentSync,
  isExperimentValueReady
} from './utils';

/**
 * @remarks Optimizely query parameter has presedence over optimizely session value
 * @returns Experiment value from query or optimizely sdk
 */
export const useActiveExperiment = (): Experiment | null => {
  const [experiment, setExperiment] = useState<Experiment | null>(
    /**
     * Note that this is only here because of current workaround where the
     * OptimizelyProvider waits for the SDK to be ready before rendering children
     */
    isExperimentValueReady() ? getExperimentSync() : null
  );

  // Normally, this would update the value when SDK is ready
  useEffect(() => {
    getExperiment().then(setExperiment);
  }, []);

  return experiment;
};

/**
 * @remarks Feature query parameter has presedence over experiment value
 * @returns Feature target value for this session
 */
export const useTestFeature = (toggleKey: FeatureToggle) => {
  const experiment = useActiveExperiment();
  const { testType, optimizely } = featureToggles[toggleKey];
  const valueFromQuery = getQueryParam(toggleKey, testType);

  if (valueFromQuery) {
    return testType === TestType.AB
      ? tryParse<boolean>(valueFromQuery)
      : optimizely?.variations[valueFromQuery]?.value ?? null;
  }

  if (experiment && optimizely && experiment.name === optimizely.key) {
    const { variations } = optimizely; // Variations defined in feature toggle
    const { variation } = experiment; // Session variation key from optimizely

    return variations[variation]?.value ?? null;
  }

  return null;
};

/**
 *
 * @returns Active experiment in enabler format
 */
export const useEnablerExperiment = () => {
  const exp = useActiveExperiment();
  if (exp) return { name: exp.name, group: exp.variation };
};
